import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EmptyState } from '@/components/feedback/EmptyState'
import { Card, CardHeader, CardSubtitle, CardTitle } from '@/components/ui/Card'
import { Sheet } from '@/components/ui/Sheet'
import { Tag } from '@/components/ui/Tag'
import { useTableFits } from '@/features/cases/components/legs/useTableFits'
import { useCasePayments } from '@/features/finance/api/useFinance'
import { PaymentActs } from '@/features/finance/components/PaymentActs'
import { PaymentAllocations } from '@/features/finance/components/PaymentAllocations'
import { PaymentStatusTag } from '@/features/finance/components/PaymentStatusTag'
import { useFormat } from '@/i18n/useFormat'
import { cn } from '@/lib/cn'
import { formatMoney, formatNumber } from '@/lib/format'
import { unitLabel } from '@/lib/legUnits'
import type {
  CaseLeg,
  CaseSuppliers,
  Money,
  Payment,
  SupplierPaymentSlice,
  SupplierSlice,
} from '@/types/domain'

/* «Платежи поставщикам» (S-11 revision, direction A «Реестр», visual 6): the
 * case's outgoing money grouped by supplier, rendered from the server's
 * `suppliers` block and from nothing else — every figure on it is served, and
 * no row is matched to a supplier here (AD-9; the payment's typed name is never
 * a key).
 *
 *  · For a reader who sees counter-agents: one group per supplier, its name
 *    and «долг · оплачено · остаток» on the group row, one row per slice under
 *    it — date, «Плечо N · plate» or «—», the reference, the slice amount in
 *    the payment's own currency and the status. A supplier with debt and no
 *    slice says «Платежей нет»; a supplier paid in full is still a group. The
 *    last group, «Без поставщика / не разнесено», holds the lines that name no
 *    supplier, the slices settling them, and every outgoing payment's
 *    UNALLOCATED REMAINDER — attention only, in no figure (AD-11).
 *  · For Sales (AD-6): no groups and no names. The same slices, flat and
 *    oldest first, under «Поставщики скрыты» — the server withholds the groups
 *    whole, because the count of suppliers is itself the disclosure.
 *
 * A ROW OPENS ITS PAYMENT (the reference is the control): a sheet with the
 * payment's allocations and acts. The payment's acts also render ONCE on the
 * ledger — on its remainder row when it has one, which is where «Разнести»
 * lands, otherwise on its first row — and every other row of a split payment
 * says «часть платежа …» instead, so a payment split across two suppliers is
 * never confirmed from two places.
 *
 * A PHONE gets cards, never a sideways table: `useTableFits` mounts EITHER the
 * table OR one card per row, so a row's acts exist once (frontend/README.md). */

/** A row of the ledger, in render order, with where it sits. */
interface LedgerRow {
  key: string
  slice: SupplierSlice & { counterpartyName?: string }
}

/** One block of rows: a supplier's group, the unattributed one, or — for a
 *  reader who sees no suppliers — the one flat list. */
interface LedgerBlock {
  key: string
  /** Absent on the flat list, which has no group row. */
  head?: {
    title: string
    debt: Money
    paid: Money
    outstanding: Money
  }
  /** The unattributed group: its rows name whom the payment says it paid. */
  unattributed: boolean
  rows: LedgerRow[]
}

function rowKey(block: string, slice: SupplierSlice): string {
  return `${block}:${slice.allocationId ?? `rest:${slice.paymentId}`}`
}

/** Where each payment's acts go — its remainder row when it has one, else its
 *  first row in render order — and how many rows each payment has. */
function placeActs(blocks: LedgerBlock[]): {
  actsRow: Map<string, string>
  rowsOf: Map<string, number>
} {
  const actsRow = new Map<string, string>()
  const rowsOf = new Map<string, number>()
  const rows = blocks.flatMap((block) => block.rows)
  for (const row of rows) {
    rowsOf.set(row.slice.paymentId, (rowsOf.get(row.slice.paymentId) ?? 0) + 1)
    if (row.slice.unallocated) actsRow.set(row.slice.paymentId, row.key)
  }
  for (const row of rows) {
    if (!actsRow.has(row.slice.paymentId)) actsRow.set(row.slice.paymentId, row.key)
  }
  return { actsRow, rowsOf }
}

export function SupplierPayments({
  caseId,
  legs,
  suppliers,
}: {
  caseId: string
  /** The case's legs, for «Плечо N · plate» — slices of one composed read. */
  legs: CaseLeg[]
  suppliers: CaseSuppliers
}) {
  const { t } = useTranslation()
  const format = useFormat()
  const tableFits = useTableFits()
  /* The PAYMENTS behind the rows, for their acts and their sheet. The same
     cache entry every payment write invalidates, so a confirmed or allocated
     payment is re-read here without anything extra being wired. Silent while
     it loads: the rows are the server's ledger and read fine without it. */
  const { data: payments } = useCasePayments(caseId)
  const paymentOf = new Map((payments ?? []).map((payment) => [payment.id, payment] as const))
  const [opened, setOpened] = useState<string | null>(null)
  const openedPayment = opened ? paymentOf.get(opened) : undefined

  /* Sales: the groups are withheld whole and the slices come flat. */
  const flat = suppliers.groups.length === 0 && suppliers.slices.length > 0

  const blocks: LedgerBlock[] = flat
    ? [
        {
          key: 'flat',
          unattributed: false,
          rows: suppliers.slices.map((slice) => ({ key: rowKey('flat', slice), slice })),
        },
      ]
    : suppliers.groups.map((group) => ({
        key: group.key,
        head: {
          title: group.attributed
            ? (group.name ?? '')
            : t('cases.costs.suppliers.unattributed'),
          debt: group.debt,
          paid: group.paid,
          outstanding: group.outstanding,
        },
        unattributed: !group.attributed,
        rows: group.slices.map((slice: SupplierPaymentSlice) => ({
          key: rowKey(group.key, slice),
          slice,
        })),
      }))

  const { actsRow, rowsOf } = placeActs(blocks)

  /* «Плечо N · Контейнер MSKU…», «Плечо N», or the dash — read off the
     record's legs, never guessed: a leg this response does not carry prints
     the dash. */
  const legLine = (slice: SupplierSlice): string => {
    const leg = slice.legId ? legs.find((candidate) => candidate.id === slice.legId) : undefined
    if (!leg) return '—'
    const unit = slice.unitId ? leg.units?.find((candidate) => candidate.id === slice.unitId) : undefined
    const name = t('cases.legs.legN', { n: leg.sequence })
    return unit ? `${name} · ${unitLabel(t, unit)}` : name
  }

  /** The reference, as the control that opens the payment — plain text while
   *  the payment itself has not been read. */
  const reference = (slice: SupplierSlice) => {
    const label = slice.unallocated
      ? `${t('finance.payments.out')} · ${slice.reference || t('common.empty')}`
      : slice.reference || t('common.empty')
    return paymentOf.has(slice.paymentId) ? (
      <button
        type="button"
        className="text-text-link hover:text-text-link-hover cursor-pointer text-left [overflow-wrap:anywhere]"
        onClick={() => setOpened(slice.paymentId)}
      >
        {label}
      </button>
    ) : (
      <span className="[overflow-wrap:anywhere]">{label}</span>
    )
  }

  /** What sits beside the reference: the remainder's tag, and — in the
   *  unattributed group only, where no supplier names the row — whom the
   *  payment says it paid. */
  const referenceNotes = (block: LedgerBlock, row: LedgerRow) => (
    <>
      {row.slice.unallocated ? (
        <Tag tone="attention" className="ml-1.5">
          {t('cases.costs.suppliers.unallocated')}
        </Tag>
      ) : null}
      {block.unattributed && row.slice.counterpartyName ? (
        <span className="text-text-muted block text-[12px] leading-snug">
          {row.slice.counterpartyName}
        </span>
      ) : null}
    </>
  )

  const status = (slice: SupplierSlice) =>
    slice.paymentStatus === 'pending' ? (
      <PaymentStatusTag status="pending" />
    ) : (
      <span className="text-text-muted text-[12px]">
        {t('finance.payments.statuses.confirmed')}
      </span>
    )

  /** The payment's acts on its one row, «часть платежа …» on its others. */
  const acts = (row: LedgerRow) => {
    const payment: Payment | undefined = paymentOf.get(row.slice.paymentId)
    if (actsRow.get(row.slice.paymentId) === row.key) {
      return payment ? <PaymentActs payment={payment} /> : null
    }
    return (rowsOf.get(row.slice.paymentId) ?? 0) > 1 ? (
      <span className="text-text-muted text-[12px] whitespace-nowrap">
        {t('cases.costs.suppliers.partOf', { reference: row.slice.reference })}
      </span>
    ) : null
  }

  /** «долг · оплачено · остаток», the served figures; the remainder in the
   *  attention tone while above zero, and named in words either way. */
  const figures = (head: NonNullable<LedgerBlock['head']>) => (
    <span className="asa-num text-text-muted flex flex-wrap items-baseline justify-end gap-x-1.5 text-[12.5px]">
      <span>
        {t('cases.costs.suppliers.debt')} {formatNumber(head.debt.amount, 2)}
      </span>
      <span aria-hidden>·</span>
      <span>
        {t('cases.costs.suppliers.paid')} {formatNumber(head.paid.amount, 2)}
      </span>
      <span aria-hidden>·</span>
      <span
        className={cn(Number(head.outstanding.amount) > 0 && 'text-attention font-semibold')}
      >
        {t('cases.costs.suppliers.outstanding')} {formatNumber(head.outstanding.amount, 2)}{' '}
        {head.outstanding.currency}
      </span>
    </span>
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('cases.costs.suppliers.title')}</CardTitle>
        {flat ? <CardSubtitle>{t('cases.costs.suppliers.hidden')}</CardSubtitle> : null}
      </CardHeader>

      {blocks.length === 0 ? (
        <EmptyState
          title={t('cases.costs.suppliers.emptyTitle')}
          description={t('cases.costs.suppliers.emptyDescription')}
          className="py-6"
        />
      ) : !tableFits ? (
        /* A phone: each group a titled list, each row a card — the same facts
           as a table row, in the order the desk reads them. */
        <div className="flex flex-col gap-4 px-[18px] pt-3 pb-4">
          {blocks.map((block) => (
            <section key={block.key} className="flex flex-col gap-2" aria-label={block.head?.title}>
              {block.head ? (
                <div className="flex flex-col gap-0.5">
                  <span className="text-text-heading text-[13.5px]">{block.head.title}</span>
                  {figures(block.head)}
                </div>
              ) : null}
              {block.rows.length === 0 ? (
                <p className="text-text-muted text-[12.5px] font-light">
                  {t('cases.costs.suppliers.noPayments')}
                </p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {block.rows.map((row) => (
                    <li
                      key={row.key}
                      className="border-border-subtle flex flex-col gap-1.5 rounded-md border px-3.5 py-3 text-[13px]"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <span className="text-text-body">
                            {format.dateShort(row.slice.receivedOn)} ·{' '}
                          </span>
                          {reference(row.slice)}
                          {referenceNotes(block, row)}
                          <span className="text-text-muted block text-[12px]">
                            {legLine(row.slice)}
                          </span>
                        </div>
                        <span className="flex shrink-0 flex-col items-end gap-1">
                          <span className="asa-num text-text-heading">
                            {formatMoney(row.slice.amount.amount, row.slice.amount.currency)}
                          </span>
                          {status(row.slice)}
                        </span>
                      </div>
                      <div className="flex justify-end empty:hidden">{acts(row)}</div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      ) : (
        <div className="asa-scroll relative mt-3 overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="border-line border-b">
                {(['date', 'leg', 'reference', 'amount', 'status'] as const).map((column) => (
                  <th
                    key={column}
                    scope="col"
                    className={cn(
                      'asa-eyebrow h-9 px-3 first:pl-[18px]',
                      column === 'amount' && 'text-right',
                    )}
                  >
                    {t(`cases.costs.suppliers.columns.${column}`)}
                  </th>
                ))}
                <th scope="col" className="h-9 px-[18px]">
                  <span className="sr-only">{t('cases.costs.suppliers.columns.acts')}</span>
                </th>
              </tr>
            </thead>
            {blocks.map((block) => (
              <tbody key={block.key} className="border-line-hair border-b last:border-b-0">
                {block.head ? (
                  <tr>
                    <th
                      scope="rowgroup"
                      colSpan={6}
                      className="px-[18px] pt-3 pb-1.5 text-left font-normal"
                    >
                      <span className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                        <span className="text-text-heading text-[13.5px]">{block.head.title}</span>
                        {figures(block.head)}
                      </span>
                    </th>
                  </tr>
                ) : null}
                {block.rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-text-muted px-[18px] pb-3 text-[12.5px] font-light">
                      {t('cases.costs.suppliers.noPayments')}
                    </td>
                  </tr>
                ) : (
                  block.rows.map((row) => (
                    <tr key={row.key} className="text-[13px]">
                      <td className="text-text-body px-3 py-2 pl-[18px] align-top whitespace-nowrap">
                        {format.dateShort(row.slice.receivedOn)}
                      </td>
                      <td className="text-text-body px-3 py-2 align-top whitespace-nowrap">
                        {legLine(row.slice)}
                      </td>
                      <td className="px-3 py-2 align-top">
                        {reference(row.slice)}
                        {referenceNotes(block, row)}
                      </td>
                      <td className="asa-num text-text-heading px-3 py-2 text-right align-top whitespace-nowrap">
                        {formatMoney(row.slice.amount.amount, row.slice.amount.currency)}
                      </td>
                      <td className="px-3 py-2 align-top whitespace-nowrap">{status(row.slice)}</td>
                      <td className="px-[18px] py-2 text-right align-top">{acts(row)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            ))}
          </table>
        </div>
      )}

      {/* The payment a row opened: its allocations and its acts, as the
          Finance page's sheet shows them. Gated on the ROW, so a payment that
          left the list under a refetch closes rather than rendering nothing. */}
      {openedPayment ? (
        <Sheet
          open
          onClose={() => setOpened(null)}
          title={t('finance.payments.detailTitle', { reference: openedPayment.reference })}
        >
          <PaymentAllocations payment={openedPayment} />
        </Sheet>
      ) : null}
    </Card>
  )
}
